/* eslint-disable react-hooks/exhaustive-deps */
/*
  AdminAIOrch — Stage B admin UI.

  Two panes:
    1. Provider routing — default model + per-purpose model override
    2. Prompts — list keys → versions → edit body / promote / retire,
       plus per-version analytics (runs · success rate · avg latency).
*/
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Cpu, Layers, GitBranch, Activity, CheckCircle2, Save } from "lucide-react";

const COMMON_MODELS = [
  "gpt-5.1", "gpt-4o", "gpt-4o-mini",
  "claude-sonnet-4-5", "claude-haiku-4-5",
  "gemini-2.5-pro", "gemini-2.5-flash",
];

function ProviderRouting({ purposeKeys }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = async () => { setCfg((await api.get("/admin/ai/config")).data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      const purposes = {};
      for (const [k, v] of Object.entries(cfg.purposes || {})) {
        if (!v?.model) continue;
        const entry = { model: v.model, provider: v.provider || undefined };
        if (v.priority_model) {
          entry.priority_model = v.priority_model;
          entry.priority_provider = v.priority_provider || undefined;
        }
        purposes[k] = entry;
      }
      const r = await api.put("/admin/ai/config", {
        default_model: cfg.default_model,
        default_provider: cfg.default_provider,
        purposes,
      });
      setCfg(r.data);
      setMsg("Saved.");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) { setMsg(e?.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };

  if (!cfg) return <p className="text-cocoa/55 text-sm">Loading routing…</p>;

  const setPurpose = (k, model) => {
    const next = { ...(cfg.purposes || {}) };
    if (!model) delete next[k]; else next[k] = { ...(next[k] || {}), model };
    setCfg({ ...cfg, purposes: next });
  };
  const setPriorityModel = (k, priority_model) => {
    const next = { ...(cfg.purposes || {}) };
    const cur = next[k] || {};
    // Only persist priority_model if a base model is set
    if (!cur.model) return;
    if (!priority_model) {
      const { priority_model: _pm, priority_provider: _pp, priority_fallbacks: _pf, ...rest } = cur;
      next[k] = rest;
    } else {
      next[k] = { ...cur, priority_model };
    }
    setCfg({ ...cfg, purposes: next });
  };

  // Always render at least the known canonical purposes even if API list is empty.
  const FALLBACK_KEYS = [
    "advisor", "quick_insight", "daily_check_in", "planner", "draft",
    "essay", "essay_factcheck", "archetype_detail", "archetype_factcheck",
    "mentor_sb", "mentor_sd",
  ];
  const knownPurposes = (purposeKeys && purposeKeys.length > 0) ? purposeKeys : FALLBACK_KEYS;

  return (
    <section data-testid="admin-ai-routing" className="ss-card p-6">
      <div className="flex items-center gap-2">
        <Cpu size={14} className="text-mauve" />
        <p className="text-[11px] tracking-[0.35em] uppercase text-mauve">Provider routing</p>
      </div>
      <h2 className="font-heading text-2xl text-cocoa mt-1">Where each model call lands.</h2>
      <p className="text-cocoa/65 text-sm mt-1">
        Default model is used for every advisor call. Override per-purpose if you want a cheaper model
        for low-stakes flows (e.g. <em>quick_insight</em>) or a higher-tier model for editorial work.
      </p>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-[10px] tracking-[0.35em] uppercase text-cocoa/55">Default model</span>
          <input data-testid="admin-ai-default-model" list="ai-models" value={cfg.default_model || ""}
            onChange={(e) => setCfg({ ...cfg, default_model: e.target.value })}
            className="mt-1 w-full bg-ivory border border-champagne rounded-md h-11 px-3" />
        </label>
        <div>
          <span className="text-[10px] tracking-[0.35em] uppercase text-cocoa/55">Inferred provider</span>
          <p className="mt-2 text-cocoa">{cfg.default_provider || "—"}</p>
        </div>
      </div>

      <p className="mt-7 text-[10px] tracking-[0.35em] uppercase text-cocoa/55">Per-purpose override</p>
      <p className="text-[11px] text-cocoa/55 mt-1 italic">
        The <span className="text-mauve">Priority</span> column is the model routed for Premium and Luxe users (plans with <code>priority_processing</code>). Leave empty to use the standard model for everyone.
      </p>
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        {knownPurposes.map((k) => {
          const cur = cfg.purposes?.[k]?.model || "";
          const prio = cfg.purposes?.[k]?.priority_model || "";
          return (
            <div key={k} className="bg-champagne/15 rounded-md px-3 py-2.5">
              <p className="text-cocoa/85 text-sm font-medium">{k}</p>
              <div className="mt-2 grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1.5">
                <span className="text-[10px] tracking-[0.25em] uppercase text-cocoa/55">Standard</span>
                <input data-testid={`admin-ai-purpose-${k}`} list="ai-models" value={cur}
                  onChange={(e) => setPurpose(k, e.target.value)}
                  placeholder="(use default)"
                  className="bg-ivory border border-champagne rounded-md h-9 px-2 text-sm w-full min-w-0" />
                <span className="text-[10px] tracking-[0.25em] uppercase text-mauve/80">Priority</span>
                <input
                  data-testid={`admin-ai-priority-${k}`}
                  list="ai-models"
                  value={prio}
                  onChange={(e) => setPriorityModel(k, e.target.value)}
                  placeholder={cur ? "(same as standard)" : "(set standard first)"}
                  disabled={!cur}
                  className="bg-ivory border border-champagne rounded-md h-9 px-2 text-sm w-full min-w-0 disabled:opacity-40"
                />
              </div>
            </div>
          );
        })}
      </div>
      <datalist id="ai-models">{COMMON_MODELS.map((m) => <option key={m} value={m} />)}</datalist>

      <div className="mt-6 flex items-center gap-3">
        <button data-testid="admin-ai-save-routing" disabled={busy} onClick={save}
          className="ss-pill ss-pill-primary h-10 px-5 disabled:opacity-50">
          <Save size={13} /> Save routing
        </button>
        {msg && <span className="text-cocoa/70 text-sm">{msg}</span>}
      </div>
    </section>
  );
}

function PromptVersionsCard({ pkey, onClose }) {
  const [versions, setVersions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [editing, setEditing] = useState(null); // version number or "new"
  const [draft, setDraft] = useState({ body: "", model_hint: "", notes: "" });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get(`/admin/prompts/${pkey}`).then((r) => r.data?.versions || []),
      api.get(`/admin/prompts/${pkey}/analytics?days=30`).then((r) => r.data).catch(() => null),
    ]);
    setVersions(a); setAnalytics(b);
  };
  useEffect(() => { load(); }, [pkey]);

  const beginNew = () => {
    const latest = versions[0];
    setDraft({ body: latest?.body || "", model_hint: latest?.model_hint || "", notes: "" });
    setEditing("new");
  };

  const saveDraft = async () => {
    setBusy(true); setMsg("");
    try {
      await api.post(`/admin/prompts/${pkey}`, draft);
      await load(); setEditing(null); setMsg("Draft saved.");
    } catch (e) { setMsg(e?.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };

  const promote = async (v) => {
    if (!window.confirm(`Promote v${v} of "${pkey}" to active? Existing active versions will be retired.`)) return;
    setBusy(true); setMsg("");
    try {
      await api.post(`/admin/prompts/${pkey}/${v}/promote`, { traffic_share: 100, retire_others: true });
      await load(); setMsg(`v${v} is now active.`);
    } catch (e) { setMsg(e?.response?.data?.detail || "Promote failed."); }
    finally { setBusy(false); }
  };

  const retire = async (v) => {
    if (!window.confirm(`Retire v${v} of "${pkey}"?`)) return;
    setBusy(true); setMsg("");
    try {
      await api.post(`/admin/prompts/${pkey}/${v}/retire`);
      await load(); setMsg(`v${v} retired.`);
    } catch (e) { setMsg(e?.response?.data?.detail || "Retire failed."); }
    finally { setBusy(false); }
  };

  const analyticsByVer = useMemo(() => {
    const m = {};
    (analytics?.by_version || []).forEach((r) => { m[r.version] = r; });
    return m;
  }, [analytics]);

  return (
    <div data-testid={`admin-prompt-versions-${pkey}`} className="ss-card-premium p-6 mt-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-[0.35em] uppercase text-mauve">Prompt</p>
          <h3 className="font-heading text-2xl text-cocoa mt-1">{pkey}</h3>
        </div>
        <div className="flex gap-2">
          <button data-testid={`prompt-${pkey}-new-version`} onClick={beginNew} className="ss-pill ss-pill-ghost h-9 text-xs">+ New version</button>
          <button onClick={onClose} className="ss-pill ss-pill-ghost h-9 text-xs">Close</button>
        </div>
      </div>

      {msg && <p className="mt-3 text-cocoa/70 text-sm">{msg}</p>}

      {editing === "new" && (
        <div className="mt-4 ss-card p-4 bg-blush/10">
          <p className="text-[10px] tracking-[0.35em] uppercase text-mauve">New draft</p>
          <textarea data-testid={`prompt-${pkey}-new-body`} value={draft.body} rows={10}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            className="mt-2 w-full bg-ivory border border-champagne rounded-md p-3 text-cocoa text-sm font-mono leading-relaxed" />
          <div className="mt-2 grid sm:grid-cols-2 gap-2">
            <input data-testid={`prompt-${pkey}-new-model`} value={draft.model_hint}
              onChange={(e) => setDraft({ ...draft, model_hint: e.target.value })}
              placeholder="Preferred model (e.g. gpt-5.1)" className="bg-ivory border border-champagne rounded-md h-10 px-3 text-sm" />
            <input value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              placeholder="Notes (e.g. shorter tone test)" className="bg-ivory border border-champagne rounded-md h-10 px-3 text-sm" />
          </div>
          <div className="mt-3 flex gap-2 justify-end">
            <button onClick={() => setEditing(null)} className="ss-pill ss-pill-ghost h-9 text-xs">Cancel</button>
            <button data-testid={`prompt-${pkey}-save-new`} disabled={busy || draft.body.length < 10}
              onClick={saveDraft} className="ss-pill ss-pill-primary h-9 text-xs disabled:opacity-50">
              Save draft
            </button>
          </div>
        </div>
      )}

      <div className="mt-5 space-y-3">
        {versions.map((v) => {
          const a = analyticsByVer[v.version];
          return (
            <div key={v.version} data-testid={`prompt-${pkey}-v${v.version}`} className="ss-card p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <span className="font-heading text-xl text-cocoa">v{v.version}</span>
                  <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full ${
                    v.status === "active" ? "bg-sage/30 text-cocoa" :
                    v.status === "draft" ? "bg-champagne text-cocoa" :
                    "bg-cocoa/10 text-cocoa/55"}`}>
                    {v.status}{v.status === "active" && v.traffic_share < 100 ? ` · ${v.traffic_share}%` : ""}
                  </span>
                  {v.model_hint && <span className="text-cocoa/55 text-xs">{v.model_hint}</span>}
                </div>
                <div className="flex gap-2">
                  {v.status !== "active" && (
                    <button data-testid={`prompt-${pkey}-promote-${v.version}`} onClick={() => promote(v.version)}
                      className="ss-pill ss-pill-primary h-8 text-[11px]"><CheckCircle2 size={12} /> Promote</button>
                  )}
                  {v.status === "active" && (
                    <button data-testid={`prompt-${pkey}-retire-${v.version}`} onClick={() => retire(v.version)}
                      className="ss-pill ss-pill-ghost h-8 text-[11px]">Retire</button>
                  )}
                </div>
              </div>
              {a && (
                <div className="mt-2 flex flex-wrap gap-4 text-xs text-cocoa/65">
                  <span><Activity size={11} className="inline mr-1" /> {a.runs} runs / 30d</span>
                  <span>{(a.success_rate * 100).toFixed(1)}% ok</span>
                  <span>{a.avg_latency_ms}ms avg</span>
                </div>
              )}
              <details className="mt-3">
                <summary className="text-mauve text-xs cursor-pointer">View prompt body</summary>
                <pre className="mt-2 text-xs whitespace-pre-wrap text-cocoa/75 bg-ivory border border-champagne rounded-md p-3 max-h-80 overflow-auto">{v.body}</pre>
              </details>
              {v.notes && <p className="mt-2 text-xs text-cocoa/55 italic">Notes: {v.notes}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PromptsPane({ onKeysLoaded }) {
  const [keys, setKeys] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = async () => {
    const data = (await api.get("/admin/prompts")).data?.keys || [];
    setKeys(data);
    if (onKeysLoaded) onKeysLoaded(data.map((k) => k.key));
  };
  useEffect(() => { load(); }, []);

  return (
    <section data-testid="admin-ai-prompts" className="ss-card p-6 mt-6">
      <div className="flex items-center gap-2">
        <Layers size={14} className="text-mauve" />
        <p className="text-[11px] tracking-[0.35em] uppercase text-mauve">Prompts</p>
      </div>
      <h2 className="font-heading text-2xl text-cocoa mt-1">Versioned system prompts.</h2>
      <p className="text-cocoa/65 text-sm mt-1">
        Each prompt key powers a flow. Promote a new draft when you want to test a different voice or shorter format —
        a 30-day rolling success rate + latency average is recorded per version.
      </p>

      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {keys.map((k) => (
          <button key={k.key} data-testid={`admin-prompt-key-${k.key}`}
            onClick={() => setSelected(k.key)}
            className={`text-left ss-card p-4 hover:bg-blush/10 transition-colors ${selected === k.key ? "ring-1 ring-mauve" : ""}`}>
            <div className="flex items-center justify-between">
              <span className="font-heading text-lg text-cocoa">{k.key}</span>
              <GitBranch size={12} className="text-cocoa/40" />
            </div>
            <div className="mt-1 flex gap-2 text-[10px] tracking-widest uppercase text-cocoa/55">
              <span>{k.versions} version{k.versions === 1 ? "" : "s"}</span>
              {k.active > 0 && <span className="text-sage-dark">{k.active} active</span>}
            </div>
            {k.model_hint && <p className="mt-1 text-xs text-cocoa/55">{k.model_hint}</p>}
          </button>
        ))}
      </div>

      {selected && <PromptVersionsCard pkey={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}

export function AdminAIOrch() {
  const [purposeKeys, setPurposeKeys] = useState([]);
  return (
    <div data-testid="admin-ai-orch">
      <ProviderRouting purposeKeys={purposeKeys} />
      <PromptsPane onKeysLoaded={setPurposeKeys} />
    </div>
  );
}
